import * as Sentry from "@sentry/react";
import { useCallback } from "react";

import { ScenarioField } from "../types/field";
import { generateExcelTemplate } from "../utils";
import { useFetchExcelTemplate } from "./useFetchExcelTemplate";

export interface UseExcelTemplateDownloadOptions {
  scenarioId?: string;
  wells: Array<{ uwid: string }>;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export interface UseExcelTemplateDownloadReturn {
  isDownloading: boolean;
  downloadError: string | null;
  handleDownloadTemplate: (selectedFields: Array<ScenarioField>) => void;
}

/**
 * Custom hook for handling Excel template downloads
 * Supports both API-based downloads (with scenarioId) and client-side generation
 * Separates business logic from UI for better testability and security
 */
export const useExcelTemplateDownload = ({
  scenarioId,
  wells,
  onSuccess,
  onError
}: UseExcelTemplateDownloadOptions): UseExcelTemplateDownloadReturn => {
  const { mutate: fetchTemplate, isLoading: isDownloading } = useFetchExcelTemplate();

  const handleDownloadTemplate = useCallback(
    (selectedFields: Array<ScenarioField>) => {
      // Validate fields are selected
      if (selectedFields.length === 0) {
        onError?.("Please select at least one field to generate a template");
        return;
      }

      // If scenarioId is provided, use the API to fetch the template
      if (scenarioId) {
        const fieldProperties = selectedFields.map((field) => field.field);
        fetchTemplate(
          {
            fields: fieldProperties,
            scenarioId
          },
          {
            onSuccess: () => {
              onSuccess?.();
            },
            onError: (error) => {
              // Don't expose raw error messages for security
              const errorMessage = "Failed to download template. Please try again.";
              onError?.(errorMessage);

              // Log actual error for debugging
              Sentry.captureException(error, {
                tags: { component: "StickDataManagement", action: "template_download" }
              });
            }
          }
        );
      } else {
        // Fallback to client-side generation if no scenarioId
        try {
          generateExcelTemplate(wells, selectedFields);
          onSuccess?.();
        } catch (error) {
          onError?.("Failed to generate template. Please try again.");

          // Log actual error for debugging
          Sentry.captureException(error, {
            tags: { component: "StickDataManagement", action: "template_generation" }
          });
        }
      }
    },
    [scenarioId, wells, fetchTemplate, onSuccess, onError]
  );

  return {
    isDownloading,
    downloadError: null, // Can be extended if needed
    handleDownloadTemplate
  };
};
